import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Edit2, Eye, EyeOff, Plus, Power, Save, Search, Settings, Trash2, X } from 'lucide-react';
import { actualizarUsuario, eliminarUsuario, obtenerUsuarios, registrarUsuario, toggleUsuarioEstado } from '../api/usuarios';
import { useNotify } from '../components/NotificationProvider';
import { isSuperAdminRole, isValidEmail, isValidPhone } from '../utils/helpers';

const emptyForm = {
  id: null,
  nombreUsuario: '',
  nombreCompleto: '',
  telefono: '',
  correo: '',
  password: '',
  confirmPassword: '',
  rol: 'usuario',
  estado: 'activo',
  fechaNacimiento: ''
};

const roles = [
  { value: 'usuario', label: 'Cliente' },
  { value: 'admin', label: 'Manicurista' },
  { value: 'super_admin', label: 'Super admin' }
];

const estados = [
  { value: 'activo', label: 'Activo' },
  { value: 'inactivo', label: 'Inactivo' }
];

const normalize = (value) => String(value || '').trim();
const normalizeSearch = (value) => normalize(value).toLowerCase();
const USERS_PER_PAGE = 5;
const roleLabel = (role) => roles.find((item) => item.value === String(role || '').toLowerCase())?.label || 'Cliente';

const AdminSettings = ({ user }) => {
  const { success: notifySuccess, error: notifyError } = useNotify();
  const [usuarios, setUsuarios] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visibleHashId, setVisibleHashId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const isEditing = !!form.id;

  const usuariosFiltrados = (() => {
    const q = normalizeSearch(searchTerm);
    const role = normalize(roleFilter);
    return usuarios.filter((item) => {
      const matchesSearch = !q ||
        [item.NombreUsuario, item.NombreCompleto, item.Correo, item.Telefono]
          .some((value) => normalizeSearch(value).includes(q));
      const matchesRole = !role || normalize(item.Rol) === role;
      return matchesSearch && matchesRole;
    });
  })();

  const totalUserPages = Math.max(1, Math.ceil(usuariosFiltrados.length / USERS_PER_PAGE));
  const safeUserPage = Math.min(userPage, totalUserPages);
  const pageStartIndex = (safeUserPage - 1) * USERS_PER_PAGE;
  const visibleUsuarios = usuariosFiltrados.slice(pageStartIndex, pageStartIndex + USERS_PER_PAGE);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const data = await obtenerUsuarios({ includeHash: true, actor: user });
      setUsuarios(data);
    } catch (err) {
      notifyError(err.message || 'No se pudieron cargar los usuarios', { duration: 4500 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdminRole(user?.role)) cargarUsuarios();
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (userPage > totalUserPages) setUserPage(totalUserPages);
  }, [userPage, totalUserPages]);

  useEffect(() => {
    setUserPage(1);
  }, [searchTerm, roleFilter]);

  const validateForm = () => {
    if (normalize(form.nombreUsuario).length < 3) return 'El usuario debe tener al menos 3 caracteres';
    if (!isValidPhone(form.telefono)) return 'Ingresa un telefono valido';
    if (!isValidEmail(form.correo)) return 'Ingresa un correo valido';
    if (!roles.some((item) => item.value === form.rol)) return 'Selecciona un rol valido';
    if (!estados.some((item) => item.value === form.estado)) return 'Selecciona un estado valido';
    if (!isEditing && form.password.length < 6) return 'La contrasena debe tener al menos 6 caracteres';
    if (isEditing && form.password && form.password.length < 6) return 'La nueva contrasena debe tener al menos 6 caracteres';
    if (form.password !== form.confirmPassword) return 'Las contrasenas no coinciden';
    return '';
  };

  const resetForm = () => {
    setForm(emptyForm);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setModalOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    const validationError = validateForm();
    if (validationError) {
      notifyError(validationError, { duration: 4000 });
      return;
    }

    const payload = {
      nombreUsuario: normalize(form.nombreUsuario),
      nombreCompleto: normalize(form.nombreCompleto) || normalize(form.nombreUsuario),
      telefono: normalize(form.telefono),
      correo: normalize(form.correo).toLowerCase(),
      password: form.password,
      rol: form.rol,
      estado: form.estado,
      fechaNacimiento: form.fechaNacimiento || null
    };

    try {
      setSaving(true);
      const result = isEditing
        ? await actualizarUsuario(form.id, payload, user)
        : await registrarUsuario(payload, user);

      if (!result.success) {
        notifyError(result.error || result.message || 'No se pudo guardar el usuario', { duration: 4500 });
        return;
      }

      const previousUser = usuarios.find((item) => item.Id === form.id);
      const roleChanged = isEditing && previousUser &&
        normalize(previousUser.Rol).toLowerCase() !== payload.rol;
      if (isEditing && result.usuario) {
        setUsuarios((current) => current.map((item) =>
          item.Id === result.usuario.Id ? { ...item, ...result.usuario } : item
        ));
      }
      if (roleChanged) setRoleFilter('');

      notifySuccess(isEditing ? 'Usuario actualizado' : 'Usuario creado', { duration: 3000 });
      resetForm();
      setUserPage(1);
      await cargarUsuarios();
    } catch (err) {
      notifyError(err.message || 'No se pudo guardar el usuario', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setForm({
      id: item.Id,
      nombreUsuario: item.NombreUsuario || '',
      nombreCompleto: item.NombreCompleto || '',
      telefono: item.Telefono || '',
      correo: item.Correo || '',
      password: '',
      confirmPassword: '',
      rol: String(item.Rol || 'usuario').toLowerCase(),
      estado: String(item.Estado || 'activo').toLowerCase(),
      fechaNacimiento: item.FechaNacimiento ? String(item.FechaNacimiento).slice(0, 10) : ''
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setModalOpen(true);
  };

  const handleToggleEstado = async (item) => {
    try {
      setSaving(true);
      const result = await toggleUsuarioEstado(item.Id, user);
      if (!result.success) {
        notifyError(result.error || 'No se pudo cambiar el estado', { duration: 4000 });
        return;
      }
      notifySuccess(`Estado cambiado a ${result.usuario.Estado}`, { duration: 3000 });
      await cargarUsuarios();
    } catch (err) {
      notifyError(err.message || 'No se pudo cambiar el estado', { duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      setSaving(true);
      const result = await eliminarUsuario(pendingDelete.Id, user);
      if (!result.success) {
        notifyError(result.error || 'No se pudo eliminar el usuario', { duration: 4500 });
        return;
      }
      notifySuccess('Usuario eliminado', { duration: 3000 });
      setPendingDelete(null);
      await cargarUsuarios();
    } catch (err) {
      notifyError(err.message || 'No se pudo eliminar el usuario', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdminRole(user?.role)) {
    return (
      <div className="rounded-lg border border-purple-100 bg-white p-8 text-center shadow-lg">
        <h1 className="text-2xl font-bold text-purple-700">Acceso restringido</h1>
        <p className="mt-2 text-gray-600">Solo el super admin puede usar configuraciones.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-purple-100 text-purple-700">
          <Settings className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configuracion</h1>
          <p className="text-sm text-gray-600">Gestion de usuarios, roles y credenciales encriptadas.</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar usuario..."
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none text-sm"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            className="h-10 rounded-xl border-2 border-gray-200 px-3 text-sm text-gray-700 focus:border-pink-500 focus:outline-none bg-white"
          >
            <option value="">Todos los roles</option>
            {roles.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => { resetForm(); setModalOpen(true); }}
          className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 px-5 py-3 font-bold text-white shadow-lg"
        >
          <Plus className="h-5 w-5" />
          Agregar usuarios
        </button>
      </div>

      <div className="rounded-lg border border-pink-100 bg-white shadow-lg">
        <div className="border-b border-pink-100 px-5 py-4">
          <h2 className="text-lg font-bold text-purple-700">Usuarios registrados</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-purple-50">
              <tr>
                <th className="px-3 py-3 text-left font-bold text-purple-700">Usuario</th>
                <th className="px-3 py-3 text-left font-bold text-purple-700">Contacto</th>
                <th className="px-3 py-3 text-left font-bold text-purple-700">Rol</th>
                <th className="px-3 py-3 text-left font-bold text-purple-700">Estado</th>
                <th className="px-3 py-3 text-left font-bold text-purple-700">Hash</th>
                <th className="px-3 py-3 text-right font-bold text-purple-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-gray-500">Cargando...</td>
                </tr>
              ) : usuariosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                    {searchTerm ? 'Sin coincidencias' : 'Sin usuarios'}
                  </td>
                </tr>
              ) : visibleUsuarios.map((item) => {
                const active = String(item.Estado || 'activo').toLowerCase() === 'activo';
                const showHash = visibleHashId === item.Id;
                return (
                  <tr key={item.Id} className="border-b border-gray-100">
                    <td className="px-3 py-3">
                      <div className="font-semibold text-gray-900">{item.NombreUsuario}</div>
                      <div className="text-xs text-gray-500">{item.NombreCompleto || '-'}</div>
                    </td>
                    <td className="px-3 py-3 text-gray-600">
                      <div>{item.Correo || '-'}</div>
                      <div className="text-xs">{item.Telefono || '-'}</div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`rounded-lg px-2 py-1 text-xs font-bold ${
                        item.Rol === 'super_admin'
                          ? 'bg-purple-100 text-purple-700'
                          : item.Rol === 'admin'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}>
                        {roleLabel(item.Rol)}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`rounded-lg px-2 py-1 text-xs font-bold ${active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="max-w-[260px] px-3 py-3">
                      <button
                        type="button"
                        onClick={() => setVisibleHashId(showHash ? null : item.Id)}
                        className="mb-1 flex items-center gap-1 rounded-lg bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                      >
                        {showHash ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        {showHash ? 'Ocultar hash' : 'Ver hash'}
                      </button>
                      {showHash && (
                        <code className="block break-all rounded-lg bg-gray-50 p-2 text-[11px] text-gray-700">
                          {item.PasswordHash || 'No disponible'}
                        </code>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          className="rounded-lg bg-blue-100 p-2 text-blue-700 hover:bg-blue-200"
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleEstado(item)}
                          disabled={saving}
                          className="rounded-lg bg-gray-100 p-2 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                          title={active ? 'Desactivar' : 'Activar'}
                        >
                          <Power className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(item)}
                          disabled={saving}
                          className="rounded-lg bg-red-100 p-2 text-red-700 hover:bg-red-200 disabled:opacity-50"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {usuariosFiltrados.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-pink-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-600">
              Mostrando {pageStartIndex + 1}-{Math.min(pageStartIndex + USERS_PER_PAGE, usuariosFiltrados.length)} de {usuariosFiltrados.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setUserPage((page) => Math.max(1, page - 1))}
                disabled={safeUserPage === 1}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Pagina anterior"
                title="Pagina anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-20 text-center text-sm font-semibold text-gray-700">
                {safeUserPage} / {totalUserPages}
              </span>
              <button
                type="button"
                onClick={() => setUserPage((page) => Math.min(totalUserPages, page + 1))}
                disabled={safeUserPage === totalUserPages}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Pagina siguiente"
                title="Pagina siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-gray-900">Eliminar usuario</h3>
            <p className="mb-5 text-sm text-gray-600">
              Se eliminara {pendingDelete.NombreUsuario}. Esta accion no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="rounded-xl bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={saving}
                className="rounded-xl bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={handleSubmit}
            noValidate
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
          >
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-purple-700">
                {isEditing ? 'Actualizar usuario' : 'Agregar usuario'}
              </h2>
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg bg-gray-100 p-2 text-gray-700 hover:bg-gray-200"
                title="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Columna izquierda: Fecha, Rol, Estado */}
                <div className="space-y-4">
                  <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
                    Fecha de nacimiento
                    <input
                      type="date"
                      value={form.fechaNacimiento}
                      onChange={(event) => setForm((prev) => ({ ...prev, fechaNacimiento: event.target.value }))}
                      className="h-14 rounded-xl border-2 border-gray-200 px-4 font-normal text-gray-900 focus:border-pink-500 focus:outline-none"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
                    Rol
                    <select
                      value={form.rol}
                      onChange={(event) => setForm((prev) => ({ ...prev, rol: event.target.value }))}
                      className="h-14 rounded-xl border-2 border-gray-200 px-4 font-normal text-gray-900 focus:border-pink-500 focus:outline-none"
                    >
                      {roles.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
                    Estado
                    <select
                      value={form.estado}
                      onChange={(event) => setForm((prev) => ({ ...prev, estado: event.target.value }))}
                      className="h-14 rounded-xl border-2 border-gray-200 px-4 font-normal text-gray-900 focus:border-pink-500 focus:outline-none"
                    >
                      {estados.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                {/* Columna derecha: Usuario, Teléfono, Correo */}
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Nombre de usuario"
                    value={form.nombreUsuario}
                    onChange={(event) => setForm((prev) => ({ ...prev, nombreUsuario: event.target.value }))}
                    className="h-14 rounded-xl border-2 border-gray-200 px-4 focus:border-pink-500 focus:outline-none"
                    maxLength={50}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Telefono ej: +56912345678"
                    value={form.telefono}
                    onChange={(event) => setForm((prev) => ({ ...prev, telefono: event.target.value }))}
                    className="h-14 rounded-xl border-2 border-gray-200 px-4 focus:border-pink-500 focus:outline-none"
                    maxLength={30}
                    required
                  />
                  <input
                    type="email"
                    placeholder="Correo"
                    value={form.correo}
                    onChange={(event) => setForm((prev) => ({ ...prev, correo: event.target.value }))}
                    className="h-14 rounded-xl border-2 border-gray-200 px-4 focus:border-pink-500 focus:outline-none"
                    maxLength={100}
                    required
                  />
                </div>
              </div>

              {/* Contraseña al final */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={isEditing ? 'Nueva contrasena opcional' : 'Contrasena'}
                    value={form.password}
                    onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                    className="h-14 w-full rounded-xl border-2 border-gray-200 px-4 pr-12 focus:border-pink-500 focus:outline-none"
                    minLength={6}
                    required={!isEditing}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-purple-700"
                    aria-label={showPassword ? 'Ocultar contrasena' : 'Ver contrasena'}
                    title={showPassword ? 'Ocultar contrasena' : 'Ver contrasena'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirmar contrasena"
                    value={form.confirmPassword}
                    onChange={(event) => setForm((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                    className="h-14 w-full rounded-xl border-2 border-gray-200 px-4 pr-12 focus:border-pink-500 focus:outline-none"
                    minLength={6}
                    required={!isEditing || !!form.password}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-purple-700"
                    aria-label={showConfirmPassword ? 'Ocultar confirmacion de contrasena' : 'Ver confirmacion de contrasena'}
                    title={showConfirmPassword ? 'Ocultar contrasena' : 'Ver contrasena'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={resetForm}
                className="rounded-xl bg-gray-200 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 px-5 py-3 font-bold text-white shadow-lg disabled:opacity-50"
              >
                {isEditing ? <Save className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                {isEditing ? 'Guardar cambios' : 'Crear usuario'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
